import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { driverService } from '../../services/driverService';
import { fleetService } from '../../services/fleetService';
import DriverStatusBadge from '../../components/drivers/DriverStatusBadge';
import DataTable from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import { Search, Filter, Eye, UserPlus, Edit, Trash } from 'lucide-react';

const DriversPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [drivers, setDrivers] = useState([]);
    const [fleets, setFleets] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [totalDrivers, setTotalDrivers] = useState(0);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        perPage: 10
    });
    const [filters, setFilters] = useState({
        status: '',
        fleet_id: user.user_type === 'admin' ? '' : user.fleet_id,
        search: ''
    });

    useEffect(() => {
        fetchDrivers();
        if (user.user_type === 'admin') {
            fetchFleets();
        }
    }, [pagination.currentPage, filters]);

    const fetchDrivers = async () => {
        setIsLoading(true);
        try {
            const { data, meta } = await driverService.getDrivers({
                page: pagination.currentPage,
                limit: pagination.perPage,
                ...filters
            });

            setDrivers(data);
            setTotalDrivers(meta.total);
        } catch (error) {
            console.error('Error fetching drivers:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchFleets = async () => {
        try {
            const fleetsData = await fleetService.getFleets();
            setFleets(fleetsData);
        } catch (error) {
            console.error('Error fetching fleets:', error);
        }
    };

    const handleSearch = (e) => {
        const value = e.target.value;
        setFilters(prev => ({ ...prev, search: value }));
    };

    const handleFleetChange = (e) => {
        const value = e.target.value;
        setFilters(prev => ({ ...prev, fleet_id: value }));
    };

    const handleStatusChange = (e) => {
        const value = e.target.value;
        setFilters(prev => ({ ...prev, status: value }));
    };

    const handleFilterReset = () => {
        setFilters({
            status: '',
            fleet_id: user.user_type === 'admin' ? '' : user.fleet_id,
            search: ''
        });
        setShowFilters(false);
    };

    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, currentPage: page }));
    };

    const handleViewDriver = (driverId) => {
        navigate(`/drivers/${driverId}`);
    };

    const handleEditDriver = (driverId) => {
        navigate(`/drivers/${driverId}/edit`);
    };

    const handleDeleteDriver = async (driverId) => {
        if (window.confirm('確定要刪除此司機嗎？此操作無法恢復。')) {
            try {
                await driverService.deleteDriver(driverId);
                fetchDrivers(); // 重新加載司機列表
            } catch (error) {
                console.error('Error deleting driver:', error);
            }
        }
    };

    const columns = [
        {
            header: '司機',
            accessor: 'full_name',
            cell: (row) => (
                <div className="flex items-center">
                    <div className="w-10 h-10 bg-gray-200 rounded-full mr-3 overflow-hidden">
                        {row.avatar_url && (
                            <img src={row.avatar_url} alt={row.full_name} className="w-full h-full object-cover" />
                        )}
                    </div>
                    <div>
                        <p className="font-medium">{row.full_name}</p>
                        <div className="flex items-center text-xs text-gray-500">
                            <span className="mr-2">{row.license_number}</span>
                            {row.rating && (
                                <span className="flex items-center">
                                    <svg className="w-3 h-3 text-yellow-400 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                    {row.rating.toFixed(1)}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            )
        },
        {
            header: '車隊',
            accessor: 'fleet_name',
            cell: (row) => (
                <span>{row.fleet_name}</span>
            )
        },
        {
            header: '車輛資訊',
            accessor: 'vehicle_model',
            cell: (row) => (
                <div>
                    <p>{row.vehicle_model} • {row.vehicle_color}</p>
                    <p className="text-sm text-gray-500">{row.plate_number}</p>
                </div>
            )
        },
        {
            header: '狀態',
            accessor: 'status',
            cell: (row) => (
                <DriverStatusBadge status={row.status} />
            )
        },
        {
            header: '上線時間',
            accessor: 'last_online_time',
            cell: (row) => (
                row.last_online_time ? (
                    <span className="text-sm text-gray-600">
                        {new Date(row.last_online_time).toLocaleString('zh-TW', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                        })}
                    </span>
                ) : (
                    <span className="text-sm text-gray-400">-</span>
                )
            )
        },
        {
            header: '操作',
            accessor: 'actions',
            cell: (row) => (
                <div className="flex space-x-2">
                    <button
                        onClick={() => handleViewDriver(row.driver_id)}
                        className="p-1 text-blue-500 hover:bg-blue-50 rounded"
                        title="查看詳情"
                    >
                        <Eye className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => handleEditDriver(row.driver_id)}
                        className="p-1 text-blue-500 hover:bg-blue-50 rounded"
                        title="編輯司機"
                    >
                        <Edit className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => handleDeleteDriver(row.driver_id)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                        title="刪除司機"
                    >
                        <Trash className="w-5 h-5" />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold">司機管理</h1>
                <button
                    onClick={() => navigate('/drivers/new')}
                    className="bg-blue-500 text-white px-4 py-2 rounded-md flex items-center"
                >
                    <UserPlus className="w-5 h-5 mr-2" />
                    新增司機
                </button>
            </div>

            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="flex-1 max-w-md relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
                                placeholder="搜尋司機姓名或車牌號碼..."
                                value={filters.search}
                                onChange={handleSearch}
                            />
                        </div>

                        {user.user_type === 'admin' && fleets.length > 0 && (
                            <select
                                className="border border-gray-300 rounded-md px-3 py-2"
                                value={filters.fleet_id}
                                onChange={handleFleetChange}
                            >
                                <option value="">所有車隊</option>
                                {fleets.map(fleet => (
                                    <option key={fleet.fleet_id} value={fleet.fleet_id}>
                                        {fleet.fleet_name}
                                    </option>
                                ))}
                            </select>
                        )}

                        <select
                            className="border border-gray-300 rounded-md px-3 py-2"
                            value={filters.status}
                            onChange={handleStatusChange}
                        >
                            <option value="">所有狀態</option>
                            <option value="online">在線</option>
                            <option value="offline">離線</option>
                            <option value="on_trip">行程中</option>
                            <option value="on_break">休息中</option>
                        </select>

                        {(filters.status !== '' || filters.search !== '' || (user.user_type === 'admin' && filters.fleet_id !== '')) && (
                            <button
                                onClick={handleFilterReset}
                                className="text-blue-500 hover:underline text-sm"
                            >
                                重置篩選
                            </button>
                        )}
                    </div>
                </div>

                {isLoading ? (
                    <div className="py-12 flex justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    </div>
                ) : (
                    <>
                        <DataTable
                            columns={columns}
                            data={drivers}
                            emptyMessage="無符合條件的司機"
                        />
                        <div className="p-4 border-t border-gray-200">
                            <Pagination
                                currentPage={pagination.currentPage}
                                totalItems={totalDrivers}
                                perPage={pagination.perPage}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default DriversPage;